import type { BeforeSendFn } from "posthog-js";

// A signing link is a bearer credential - anyone holding `id` and `key` can
// open the document - and signer email addresses appear in thrown messages.
// Neither belongs in a third-party analytics store, so both are removed from
// every event before it leaves the browser.
//
// The credential is not only in the query string. These URLs carry the signer's
// token TWICE - as `?id=` and as a path segment - so the query, the fragment and
// the path are all scrubbed. See redactPathSegments for why the path rule is
// cross-referential rather than name-based.
//
// MAINTAINERS: when adding a query parameter anywhere in the app that carries
// a credential, an identifier for a person, or anything else that should not
// leave the browser, add its name to ALWAYS_SENSITIVE_PARAMS below. A credential
// placed in a PATH segment needs no list entry - it is caught by matching the
// query value or by TOKEN_MIN_LENGTH - but it does need a test, because the
// length backstop is the only thing standing behind it.

// URL-safe so that rewriting a query parameter does not introduce
// percent-encoding into $current_url.
const REDACTED = "__redacted__";

// Compared against lower-cased parameter names; every entry must be lowercase.
const ALWAYS_SENSITIVE_PARAMS = [
  "key",
  "token",
  "access_token",
  "refresh_token",
  "signature",
  "sig",
  "otp",
  "password",
  "email",
  // OAuth redirect parameters. None of these ends in "id", "token", "key" or
  // "secret", so TOKEN_LIKE_SUFFIXES never reaches them and they were passing
  // through at ANY length - the length backstop does not apply to a name it
  // does not match. They are read straight off window.location.search by
  // CloudStorageOAuthCallback (code, state, session_state) and by the MCP
  // OAuthConsent screen (state), so they land in $current_url on a route the
  // scrubber sees.
  //
  // "code" is the authorization code: single-use, but exchangeable for an
  // access token by whoever presents it first, and the callback's own comment
  // records that a replayed code fails - which is the same thing said from the
  // other side. "state" is the CSRF binding. "session_state" is listed
  // separately because matching here is exact, not by suffix, so "state" does
  // not cover it.
  "code",
  "state",
  "session_state",
];

// Names too generic to redact outright: `?id=123` routing a page is not a
// secret, but the signing route's `id` is a request token. Matching by suffix
// covers documentId, requestId, sessionId and the like without a hand-kept
// list, and length separates a routing value from a credential.
const TOKEN_LIKE_SUFFIXES = ["id", "token", "key", "secret"];
const TOKEN_MIN_LENGTH = 20;

// Quantifiers are bounded: the unbounded form is quadratic on a long
// non-matching run (~750ms for 50k characters), which a single oversized
// property could otherwise trigger on the capture path.
const EMAIL_PATTERN = /[\w.+-]{1,64}@[\w-]{1,255}\.[\w.-]{1,255}/g;

// Deep enough for any real $exception_list payload. Anything deeper is
// replaced rather than passed through, so an unexpectedly nested structure
// cannot smuggle an unscrubbed value out.
const MAX_DEPTH = 12;
const TOO_DEEP = "[redacted:depth]";

// Used only to give a relative URL a parseable base; never emitted.
const RELATIVE_BASE = "http://relative.invalid";

// Name-only test, with no length gate. Used for the path cross-reference, where
// length is the wrong question - see the snapshot in redactUrl.
const isCredentialParamName = (name: string): boolean => {
  const lower = name.toLowerCase();
  return (
    ALWAYS_SENSITIVE_PARAMS.indexOf(lower) !== -1 ||
    TOKEN_LIKE_SUFFIXES.some(
      (suffix) => lower === suffix || lower.slice(-suffix.length) === suffix,
    )
  );
};

const isSensitiveParam = (name: string, value: string): boolean => {
  const lower = name.toLowerCase();
  if (ALWAYS_SENSITIVE_PARAMS.indexOf(lower) !== -1) {
    return true;
  }
  if (value.length < TOKEN_MIN_LENGTH) {
    return false;
  }
  return TOKEN_LIKE_SUFFIXES.some(
    (suffix) => lower === suffix || lower.slice(-suffix.length) === suffix,
  );
};

// Names are collected before any rewrite: set() drops duplicate entries, which
// would desync a live URLSearchParams iterator mid-loop.
const redactParams = (params: URLSearchParams): boolean => {
  const doomed: string[] = [];

  params.forEach((value, name) => {
    if (value && isSensitiveParam(name, value) && doomed.indexOf(name) === -1) {
      doomed.push(name);
    }
  });

  doomed.forEach((name) => params.set(name, REDACTED));
  return doomed.length > 0;
};

// A fragment is never sent to a server, which is precisely why credentials get
// put there, so it is scrubbed on the same terms as the query.
const redactHash = (hash: string): { hash: string; changed: boolean } => {
  if (!hash) {
    return { hash, changed: false };
  }

  const body = hash.slice(1);
  const params = new URLSearchParams(body);

  // Presence of "=" is not enough to call this key=value form: a base64 token
  // ends in padding, which parses as a name with an empty value.
  let hasPair = false;
  params.forEach((value) => {
    if (value) {
      hasPair = true;
    }
  });

  if (hasPair) {
    const changed = redactParams(params);
    return { hash: changed ? `#${params.toString()}` : hash, changed };
  }

  // Otherwise the fragment is opaque - an anchor or a bare token - and only
  // length separates the two.
  return body.length >= TOKEN_MIN_LENGTH
    ? { hash: `#${REDACTED}`, changed: true }
    : { hash, changed: false };
};

// Known limit: a RELATIVE path with a credential segment and no query or
// fragment ("/callback/sign/pdf/request/<token>") is not recognised here and is
// returned untouched. Every URL on the capture path is absolute, so the real
// case is covered; widening this to any string starting with "/" would pull in
// ordinary filesystem-looking strings and mangle them.
const looksLikeUrl = (value: string): boolean =>
  value.indexOf("://") !== -1 ||
  (value.charAt(0) === "/" &&
    (value.indexOf("?") !== -1 || value.indexOf("#") !== -1));

// A UUID clears TOKEN_MIN_LENGTH on its own, so length covers it; the pattern
// is here to state the intent rather than to add reach.
const UUID_SEGMENT =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The credential is in the path as well as the query.
 *
 * The module comment inherited the assumption in `analyticsPaths.ts` that "the credential always
 * lives in the query string". That holds for the view routes that file governs. It does not hold
 * for the API URLs this scrubber sees: `SignRequestService.ts:486` builds
 * `${urls.SIGN_PDF}/${token}?id=${token}&key=...`, so the same token is a path segment *and* the
 * `id` parameter. Scrubbing only the query left the token in `url.pathname`, and every rebuild
 * below passes the pathname through verbatim.
 *
 * A path segment has no name to match on, so the primary rule is cross-referential: a segment
 * whose value also appears as a sensitive parameter is that credential by definition, whatever its
 * length. `TOKEN_MIN_LENGTH` and the UUID shape act as a backstop for a token that stops being
 * echoed in the query. The parameter values must be snapshotted BEFORE `redactParams` runs, since
 * that rewrites them to `REDACTED` in place and the comparison would then never match.
 */
const redactPathSegments = (
  url: URL,
  secrets: string[],
): { changed: boolean; confirmed: string[] } => {
  let changed = false;
  const confirmed: string[] = [];

  const segments = url.pathname.split("/").map((segment) => {
    if (!segment) {
      return segment;
    }
    const echoedInQuery = secrets.indexOf(segment) !== -1;
    const isSecret =
      echoedInQuery ||
      UUID_SEGMENT.test(segment) ||
      segment.length >= TOKEN_MIN_LENGTH;
    if (!isSecret) {
      return segment;
    }
    // A value under a credential-shaped parameter name that ALSO occupies a path segment is a
    // credential being passed around, not a routing number. That is positive evidence, so it is
    // reported back and redacted from the query too, whatever its length - see redactUrl.
    if (echoedInQuery && confirmed.indexOf(segment) === -1) {
      confirmed.push(segment);
    }
    changed = true;
    return REDACTED;
  });

  if (changed) {
    url.pathname = segments.join("/");
  }
  return { changed, confirmed };
};

const redactUrl = (value: string): string => {
  if (!looksLikeUrl(value)) {
    return value;
  }

  // "//host/path" carries a host even though it starts with a slash, so it is
  // rebuilt with the host intact rather than as a path-only string.
  const protocolRelative = value.slice(0, 2) === "//";
  const pathOnly = !protocolRelative && value.charAt(0) === "/";

  let url: URL;
  try {
    url = new URL(value, protocolRelative || pathOnly ? RELATIVE_BASE : undefined);
  } catch {
    return value;
  }

  // Snapshotted before redactParams rewrites the values in place.
  //
  // Deliberately uses isCredentialParamName, not isSensitiveParam: the latter applies
  // TOKEN_MIN_LENGTH, and a token shorter than that would then be absent from the snapshot and
  // survive in the path. The length gate exists to keep `?id=123` out of the blocklist, but that
  // reasoning does not transfer here - a value is only redacted from the path if it appears under
  // a credential-shaped parameter name AND occupies a path segment, which `123` on a routing URL
  // does not do.
  const secrets: string[] = [];
  url.searchParams.forEach((paramValue, name) => {
    if (paramValue && isCredentialParamName(name)) {
      secrets.push(paramValue);
    }
  });

  const { changed: pathChanged, confirmed } = redactPathSegments(url, secrets);
  const queryChanged = redactParams(url.searchParams);

  // TOKEN_MIN_LENGTH keeps `?id=123` out of the blocklist, but it also lets a SHORT credential
  // through `?id=`. Where the path has just confirmed that value is a credential, the length
  // gate is overruled for that value alone. This narrows a real hole without moving
  // TOKEN_MIN_LENGTH, which also governs the fragment and every other parameter.
  let confirmedChanged = false;
  if (confirmed.length > 0) {
    const doomed: string[] = [];
    url.searchParams.forEach((paramValue, name) => {
      if (confirmed.indexOf(paramValue) !== -1 && doomed.indexOf(name) === -1) {
        doomed.push(name);
      }
    });
    doomed.forEach((name) => url.searchParams.set(name, REDACTED));
    confirmedChanged = doomed.length > 0;
  }
  const { hash, changed: hashChanged } = redactHash(url.hash);
  url.hash = hash;

  // Returning the original when nothing matched avoids re-encoding a URL into
  // a second spelling of the same page.
  if (!pathChanged && !queryChanged && !confirmedChanged && !hashChanged) {
    return value;
  }

  if (protocolRelative) {
    return `//${url.host}${url.pathname}${url.search}${url.hash}`;
  }
  return pathOnly
    ? `${url.pathname}${url.search}${url.hash}`
    : url.toString();
};

// Error messages from fetch and axios wrappers embed the request URL in prose,
// so the whole string does not parse as a URL and the credential would survive
// untouched. Each URL-shaped run is redacted in place instead.
const URL_IN_TEXT = /https?:\/\/[^\s"'<>\\]+/g;
const TRAILING_PUNCTUATION = /[.,;:!?)\]}]+$/;

const redactUrlsWithinText = (value: string): string =>
  value.replace(URL_IN_TEXT, (match) => {
    const trailing = TRAILING_PUNCTUATION.exec(match);
    const core = trailing ? match.slice(0, -trailing[0].length) : match;
    return redactUrl(core) + (trailing ? trailing[0] : "");
  });

const scrubString = (value: string): string => {
  // The whole-string pass preserves the exact spelling of properties that are
  // themselves a URL; the in-text pass catches one quoted inside a message.
  let redacted = redactUrl(value);
  if (redacted === value) {
    redacted = redactUrlsWithinText(value);
  }
  return redacted.replace(EMAIL_PATTERN, REDACTED);
};

// Only arrays and plain objects are walked. A Date, Error, Map or RegExp has
// no useful own enumerable keys and would otherwise be rebuilt as {}.
const isPlainObject = (value: unknown): boolean => {
  if (value === null || typeof value !== "object") {
    return false;
  }
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

const scrubValue = (value: unknown, depth: number): unknown => {
  if (typeof value === "string") {
    return scrubString(value);
  }

  if (value === null || typeof value !== "object") {
    return value;
  }

  const walkable = Array.isArray(value) || isPlainObject(value);
  if (!walkable) {
    return value;
  }

  if (depth >= MAX_DEPTH) {
    return TOO_DEEP;
  }

  if (Array.isArray(value)) {
    return value.map((entry) => scrubValue(entry, depth + 1));
  }

  const source = value as Record<string, unknown>;
  const result: Record<string, unknown> = {};
  Object.keys(source).forEach((key) => {
    // Assigning "__proto__" through bracket notation would reassign the
    // prototype of `result` rather than adding a property to it.
    if (key === "__proto__") {
      return;
    }
    result[key] = scrubValue(source[key], depth + 1);
  });
  return result;
};

export const scrubSensitiveData: BeforeSendFn = (captureResult) => {
  if (!captureResult?.properties) {
    return captureResult;
  }

  return {
    ...captureResult,
    properties: scrubValue(captureResult.properties, 0) as Record<string, any>,
  };
};
